import React, { useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import OdpovediHasicu from '../components/OdpovediHasicu';
import { Odpoved, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';

const ALLOWED_STYLES: DisplejStylOdpovedi[] = ['STRIPE', 'BACKGROUND_TINTED'];

const FAMILY_NAMES = [
    'Procházka', 'Kořínek', 'Janda', 'Hájek', 'Vlček', 'Bílý', 'Němec', 'Malý',
    'Novák', 'Svoboda', 'Dvořák', 'Černý', 'Kučera', 'Veselý', 'Horák', 'Marek',
    'Pospíšil', 'Jelínek', 'Král', 'Růžička', 'Beneš', 'Fiala', 'Sedláček', 'Doležal',
    'Urban', 'Kovář', 'Kratochvíl', 'Blažek', 'Kolář', 'Navrátil',
];
const FIRST_NAMES = [
    'Lukáš', 'Marek', 'Filip', 'Tomáš', 'Petr', 'Jan', 'Robert', 'Ondřej',
    'Jakub', 'Adam', 'Stanislav', 'Martin', 'David', 'Vojtěch', 'Daniel', 'Patrik',
    'Matěj', 'Michal', 'Aleš', 'Roman', 'Karel', 'Pavel', 'Jaroslav', 'Miroslav',
    'Zdeněk', 'František', 'Václav', 'Milan', 'Jiří', 'Radek',
];

const buildMockOdpovedi = (jeduCount: number, nejeduCount: number): Odpoved[] => {
    const total = jeduCount + nejeduCount;
    const baseTime = new Date('2026-04-28T19:42:00Z').getTime();
    const out: Odpoved[] = [];
    for (let i = 0; i < total; i++) {
        const jmeno = `${FIRST_NAMES[i % FIRST_NAMES.length]} ${FAMILY_NAMES[i % FAMILY_NAMES.length]}`;
        const isJedu = i < jeduCount;
        const funkceKod = i < 2 ? 'VD' : i < 6 ? 'S' : 'H';
        const funkceNazev = funkceKod === 'VD' ? 'Velitel družstva'
            : funkceKod === 'S' ? 'Strojník' : 'Hasič';
        const specializace =
            i === 0 ? [{ id: 1, kod: 'NDT', nazev: 'Nositel dýchací techniky' }]
            : i === 2 ? [{ id: 2, kod: 'PILAR', nazev: 'Obsluhovatel motorové pily' }]
            : i === 3 ? [
                { id: 1, kod: 'NDT', nazev: 'Nositel dýchací techniky' },
                { id: 2, kod: 'PILAR', nazev: 'Obsluhovatel motorové pily' },
            ]
            : [];
        out.push({
            id: i + 1,
            poplachId: 1,
            uzivatelId: i + 1,
            jmeno,
            odpoved: isJedu ? 'JEDU' : 'NEJEDU',
            cas: new Date(baseTime + i * 4000).toISOString(),
            funkce: [{ id: i + 1, kod: funkceKod, nazev: funkceNazev }],
            funkceAsString: funkceNazev,
            specializace,
        } as Odpoved);
    }
    return out;
};

const parseStyl = (raw: string | null): DisplejStylOdpovedi => {
    if (raw && (ALLOWED_STYLES as string[]).includes(raw)) {
        return raw as DisplejStylOdpovedi;
    }
    return 'BACKGROUND_TINTED';
};

const parsePositiveInt = (raw: string | null, fallback: number): number => {
    if (!raw) return fallback;
    const n = parseInt(raw, 10);
    if (isNaN(n) || n < 0 || n > 60) return fallback;
    return n;
};

const DevOdpovediPreviewPage: React.FC = () => {
    const [params] = useSearchParams();
    const styl = parseStyl(params.get('styl'));
    const jeduCount = parsePositiveInt(params.get('jedu'), 5);
    const nejeduCount = parsePositiveInt(params.get('nejedu'), 2);

    const odpovedi = useMemo(
        () => buildMockOdpovedi(jeduCount, nejeduCount),
        [jeduCount, nejeduCount]
    );

    return (
        <Box sx={{ p: 2, minHeight: '100vh', bgcolor: 'background.default' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Dev preview: Odpovědi
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    styl=<strong>{styl}</strong> &nbsp; jedu=<strong>{jeduCount}</strong>
                    &nbsp; nejedu=<strong>{nejeduCount}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    URL: ?styl=BACKGROUND_TINTED|STRIPE &nbsp;?jedu=N &nbsp;?nejedu=N
                </Typography>
            </Stack>
            <Box
                sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    bgcolor: 'background.paper',
                }}
            >
                <OdpovediHasicu
                    odpovedi={odpovedi}
                    showSpecializace
                    displejStyl={styl}
                    adaptivniLayout={false}
                />
            </Box>
        </Box>
    );
};

export default DevOdpovediPreviewPage;
